import React, { useState, useEffect, useMemo } from "react";

import { useAppDispatch, useAppSelector } from "../../redux/hooks/redux-hooks";
import { getAllCountries } from "../../redux/actions/CountryActions";
import EnhancedTable from "../table/Table";
import PageTitle from "../PageTitle";
import PageSearch from "../PageSearch";
import DialogBox from "../../components/dialog-box/DialogBox";
import CountryForm from "./CountryForm";
import Loader from "../../components/loader/Loader";
import { CountryModel } from "../../models/CountryModel";
import { isValidArr } from "../../utility/utils";

export type Mode = "add" | "delete";

type iData = {
  id: string;
  name: string;
  code: string;
};

const CountryPage = () => {
  const dispatch = useAppDispatch();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [mode, setMode] = useState<Mode>("add");
  const [editData, setEditData] = useState<CountryModel>();
  const [rootData, setRootData] = useState<iData[]>([]);
  const [filteredData, setFilteredData] = useState<iData[]>([]);

  const allData = useAppSelector((state) => state.country.all_Countries);
  const isCountriesFetching = useAppSelector(
    (state) => state.country.isCountriesFetching
  );

  const columns = useMemo(() => {
    return [
      {
        id: "name",
        numeric: false,
        disablePadding: false,
        label: "Country Name",
      },
      {
        id: "code",
        numeric: false,
        disablePadding: false,
        label: "Country Code",
      },
    ];
  }, []);

  //To fetch all countries
  useEffect(() => {
    dispatch(getAllCountries());
  }, [dispatch]);

  // To prepare the root data
  useEffect(() => {
    setRootData(() =>
      allData.map((rec) => ({
        id: rec.countryCode!,
        name: rec.countryName!,
        code: rec.countryCode!,
      }))
    );
  }, [allData]);

  // To set filtered data on change root data
  useEffect(() => {
    setFilteredData(rootData);
  }, [rootData]);

  const handleEdit = (id: string | number) => {
    const editRec = allData.find((rec) => rec.countryCode === id);
    setMode("delete");
    setEditData(editRec);
    setIsModalOpen(true);
  };

  const createHandler = () => {
    setMode("add");
    setEditData(undefined);
    setIsModalOpen(true);
  };

  const handleModalClose = () => {
    setIsModalOpen(false);
  };

  const searchHandler = (searchVal: string): void => {
    setFilteredData(() =>
      searchVal
        ? rootData.filter((info) =>
            Object.values(info).join("").toLowerCase().includes(searchVal)
          )
        : rootData
    );
  };

  return (
    <>
      {!isValidArr(allData) && isCountriesFetching ? <Loader /> : null}
      <PageTitle
        title="Countries"
        btnText="Add Country"
        onClick={createHandler}
      />
      <PageSearch
        placeholder="Search Countries"
        searchHandler={searchHandler}
      />
      <EnhancedTable
        columns={columns}
        data={filteredData}
        handleEdit={handleEdit}
        isEditable={false}
      />
      {isModalOpen && (
        <DialogBox
          isModalOpen={isModalOpen}
          modalTitle={mode === "add" ? "Create Country" : "Delete Country"}
          handleModalClose={handleModalClose}
        >
          <CountryForm
            mode={mode}
            data={editData}
            handleModalClose={handleModalClose}
          />
        </DialogBox>
      )}
    </>
  );
};

export default CountryPage;
